import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

export function CountUp({ to = 100, duration = 1.4, suffix = "", decimals = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(eased * to);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  return (
    <span ref={ref} className={className}>
      {val.toFixed(decimals)}
      {suffix}
    </span>
  );
}

export function MatchRing({ value = 90, size = 56, stroke = 6, color = "#06B6D4", label }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const numericVal = Math.min(100, Math.max(0, Number(value) || 0));
  const offset = c - (numericVal / 100) * c;

  return (
    <div ref={ref} className="relative inline-flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="currentColor"
          className="text-border dark:text-secondary/80"
          strokeWidth={stroke}
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.0, ease: "easeOut" }}
        />
      </svg>
      <span className="absolute font-heading font-extrabold text-foreground" style={{ fontSize: Math.max(12, size * 0.26) }}>
        {label ?? `${numericVal}%`}
      </span>
    </div>
  );
}

export function SectionLabel({ children, color = "text-electric" }) {
  return (
    <div className="flex items-center gap-3">
      <span className={`h-px w-8 bg-current ${color}`} />
      <span className={`label-caps ${color}`}>{children}</span>
    </div>
  );
}

export function Reveal({ children, delay = 0, y = 24, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function GradientText({ children, className = "" }) {
  return (
    <span className={`bg-gradient-to-r from-electric via-violet to-aqua bg-clip-text text-transparent ${className}`}>
      {children}
    </span>
  );
}
